import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavigateFunction, useLocation } from 'react-router-dom'

import { cash, creditCard } from '@/assets/images'
import Container from '@/components/atoms/container/container'
import PaymentCard from '@/components/atoms/paymentCard/paymentCard'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import { apiService } from '@/services/apiService'
import {
    getCardPaymentDebugEnabled,
    getCardPaymentEnabled
} from '@/services/deviceConfigurationService'
import { TransactionService } from '@/services/transactionService'

export default function PaymentMethodTerminalTemplate({
    navigate
}: {
    navigate: NavigateFunction
}) {
    const { t } = useTranslate()
    const location = useLocation()
    const prevState = useMemo(
        () => (location.state as Record<string, unknown> | null) ?? {},
        [location.state]
    )
    const {
        setPaymentMethod,
        setCurrentStep,
        setCashDetected,
        state: orderState
    } = useOrder()
    const { deviceConfiguration } = useCheckInternetConnection()
    const cardPaymentEnabled = getCardPaymentEnabled(deviceConfiguration)
    const cardPaymentDebugEnabled =
        getCardPaymentDebugEnabled(deviceConfiguration)
    const sessionId = orderState.sessionId || undefined

    const [cardPosStatusJson, setCardPosStatusJson] = useState<string>('')
    const [cardPosStatusLoading, setCardPosStatusLoading] = useState(false)
    const [draftAmount, setDraftAmount] = useState<number | null>(null)
    // Use the session flag for an instant check; fall back to the API result.
    // This avoids a blank white page while GetVoucherAmount resolves.
    const shouldLockToCash =
        orderState.cashDetected || (draftAmount !== null && draftAmount > 0)

    const refreshCardPaymentStatus = useCallback(async () => {
        setCardPosStatusLoading(true)
        try {
            const status = await apiService.getPosStatus(sessionId)
            setCardPosStatusJson(JSON.stringify(status, null, 2))
        } catch (e) {
            setCardPosStatusJson(
                JSON.stringify(
                    {
                        error: e instanceof Error ? e.message : 'Unknown error'
                    },
                    null,
                    2
                )
            )
        } finally {
            setCardPosStatusLoading(false)
        }
    }, [sessionId])

    useEffect(() => {
        if (!cardPaymentDebugEnabled) return
        void refreshCardPaymentStatus()
    }, [cardPaymentDebugEnabled, refreshCardPaymentStatus])

    useEffect(() => {
        let isMounted = true

        TransactionService.GetVoucherAmount()
            .then((response) => {
                if (!isMounted) return
                const amount =
                    typeof response?.amount === 'number' ? response.amount : 0
                setDraftAmount(amount)
                if (amount > 0) setCashDetected(true)
            })
            .catch(() => {
                if (!isMounted) return
                setDraftAmount(0)
            })

        return () => {
            isMounted = false
        }
    }, [setCashDetected])

    // When the device configuration disables card POS payments, the payment
    // method screen is skipped entirely and the kiosk goes straight to the
    // cash flow. The redirect runs once, replaces history so the back button
    // bypasses this screen, and the render below returns null to avoid a
    // flash of the disabled card option.
    const hasRedirectedToCashRef = useRef(false)
    useEffect(() => {
        if (cardPaymentEnabled) return
        if (hasRedirectedToCashRef.current) return
        hasRedirectedToCashRef.current = true
        setPaymentMethod('cash')
        setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
        navigate('/buy-voucher-cash', {
            replace: true,
            state: {
                ...prevState,
                voucherType: prevState.voucherType || 'gaming'
            }
        })
    }, [
        cardPaymentEnabled,
        navigate,
        prevState,
        setCurrentStep,
        setPaymentMethod
    ])

    // Cash already in the acceptor must stay on the cash flow — e.g. after a
    // history back from the insert-cash screen.
    useEffect(() => {
        if (!shouldLockToCash) return
        if (hasRedirectedToCashRef.current) return
        hasRedirectedToCashRef.current = true
        setPaymentMethod('cash')
        setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
        navigate('/buy-voucher-cash', {
            replace: true,
            state: {
                ...prevState,
                voucherType: prevState.voucherType || 'gaming'
            }
        })
    }, [
        navigate,
        prevState,
        setCurrentStep,
        setPaymentMethod,
        shouldLockToCash
    ])

    if (!cardPaymentEnabled || shouldLockToCash) {
        return null
    }

    const voucherType = prevState.voucherType
    const bettingFlow = prevState.bettingFlow
    const navigateBackUrl =
        voucherType === 'gaming'
            ? '/gaming-selection'
            : voucherType === 'betting' && bettingFlow === 'direct'
              ? '/betting-vendor-account'
              : voucherType === 'betting'
                ? '/betting-flow-selection'
                : '/welcome-with-services'

    return (
        <Container isFullHeight={true}>
            <Header navigateBackUrl={navigateBackUrl} navigationBackText={' '} />
            <div className="payment-method-terminal">
                <h1>{t('selectPaymentMethod.title')}</h1>
                <h2>{t('selectPaymentMethod.subtitle')}</h2>
                <div className="payment-methods">
                    <PaymentCard
                        image={cash}
                        text={t('selectPaymentMethod.cashPayment')}
                        // get state from the previous navigate
                        callback={() => {
                            setPaymentMethod('cash')
                            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
                            navigate('/buy-voucher-cash', {
                                replace: true,
                                state: {
                                    ...prevState,
                                    voucherType: prevState.voucherType || 'gaming'
                                }
                            })
                        }}
                    />
                    <PaymentCard
                        image={creditCard}
                        text={t('selectPaymentMethod.cardPayment')}
                        callback={() => {
                            setPaymentMethod('card')
                            setCurrentStep(VoucherPurchaseStep.CARD_PAYMENT)
                            navigate('/buy-voucher-cash', {
                                replace: true,
                                state: {
                                    ...prevState,
                                    voucherType: prevState.voucherType || 'gaming'
                                }
                            })
                        }}
                        isDisabled={false}
                    />
                </div>
                {cardPaymentDebugEnabled && (
                    <div className="payment-method-terminal__card-debug">
                        <div className="payment-method-terminal__card-debug-header">
                            <span className="payment-method-terminal__card-debug-label">
                                Card POS status
                            </span>
                            <button
                                type="button"
                                className="payment-method-terminal__card-debug-refresh"
                                onClick={() => void refreshCardPaymentStatus()}
                                disabled={cardPosStatusLoading}
                            >
                                {cardPosStatusLoading ? '…' : 'Refresh'}
                            </button>
                        </div>
                        <pre className="payment-method-terminal__card-debug-json">
                            {cardPosStatusJson ||
                                (cardPosStatusLoading ? 'Loading…' : '')}
                        </pre>
                    </div>
                )}
            </div>
            <Footer />
        </Container>
    )
}
